/* ──────────────────────────────────────────────────────────────
   Incidencias por alumno  ·  Informe + detalle de reservas
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  Collapse,
  IconButton,
  Button,
  CircularProgress,
  Tooltip as MuiTooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Visibility from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useIncidenciasAlumnoReport } from "../../../hooks/useReportJob";
import { useClientes } from "../../../hooks/useClientes";
import { getReservasIncidenciaAlumno, ReservaIncidencia, getReserva } from "../../../api/reversas";
import ReservationFormDialog from "../../../components/ReservationFormDialog";

type ReservaIncidenciaWithComment = ReservaIncidencia & {
  comentarios?: string | null;
  comentario?: string | null;
  comentariosInternos?: string | null;
  notas?: string | null;
  observaciones?: string | null;
};

function extractComentario(d: ReservaIncidencia): string {
  // admitimos camelCase y snake_case
  const aliases = [
    "comentarios",
    "comentario",
    "comentariosInternos",
    "comentarioInterno",
    "notas",
    "observaciones",
    "comentario_interno",
    "comentarios_internos",
    "observaciones_internas",
    "nota_interna",
    "notas_internas",
    "incidencia_comentario",
    "detalle_incidencia",
  ];
  const anyd = d as unknown as Record<string, unknown>;
  for (const k of aliases) {
    const v = anyd?.[k];
    if (v != null) {
      const s = String(v).trim();
      if (s) return s;
    }
  }
  return "";
}

export default function IncidenciasAlumnoPage() {
  const { t } = useTranslation<"translation">();
  /* ═════════ filtros de entrada ═════════ */
  const today = dayjs().format("YYYY-MM-DD");
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);
  const [search, setSearch] = useState("");

  /* ═════════ catálogos ═════════ */
  const { rows: alumnos } = useClientes(); // sólo rol = cliente

  /* ═════════ job resumen (incidencias por alumno) ═════════ */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useIncidenciasAlumnoReport();
  const { data: status } = useStatus(jobId);

  /* ═════════ detalle por alumno (reservas incidentadas) ═════ */
  const [openRow, setOpenRow] = useState<number>(); // alumno abierto
  const [detalles, setDetalles] = useState<Record<number, ReservaIncidencia[]>>({});

  /* ═════════ diálogo reserva seleccionada ═════════ */
  const [selId, setSelId] = useState<number | null>(null);
  const [selData, setSelData] = useState<any | null>(null);
  const [loadingSel, setLoadingSel] = useState(false);

  /* ───── generar informe ───── */
  const launch = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
    setOpenRow(undefined);
    setDetalles({});
    setSelId(null);
    setSelData(null);
  };

  /* ───── expandir / colapsar detalle ───── */
  const toggleDetalle = async (alumnoId: number) => {
    if (openRow === alumnoId) {
      setOpenRow(undefined);
      return;
    }

    /* cache-in-memory: si no lo tenemos ya, lo pedimos */
    if (!detalles[alumnoId]) {
      const data = await getReservasIncidenciaAlumno(alumnoId, desde, hasta);
      setDetalles((prev) => ({ ...prev, [alumnoId]: data }));
    }
    setOpenRow(alumnoId);
  };

  /* ───── abrir reserva concreta ───── */
  const abrirReserva = (id: number) => {
    setSelId(id);
    setSelData(null);
    setLoadingSel(true);
    getReserva(id)
      .then((r) => setSelData(r))
      .finally(() => setLoadingSel(false));
  };

  /* limpiar datos al cerrar diálogo */
  useEffect(() => {
    if (!selId) setSelData(null);
  }, [selId]);

  /* ───── resumen filtrado por búsqueda ───── */
  const rows =
    status?.result?.rows.filter((r) => r.alumno.toLowerCase().includes(search.toLowerCase())) ?? [];

  /* ═════════ UI ═════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("incidenciasAlumno.titulo")}</Typography>

      {/* ─── barra de filtros ─── */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t("incidenciasAlumno.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("incidenciasAlumno.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <TextField
          size="small"
          placeholder={t<string>("incidenciasAlumno.filtros.buscar")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={launch}>
          {t("incidenciasAlumno.filtros.generar")}
        </Button>
      </Stack>

      {/* ─── progreso worker ─── */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("incidenciasAlumno.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* ─── resumen + detalle ─── */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t("incidenciasAlumno.estado.exito", {
              count: status.result!.registros,
            })}
          </Alert>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <b>{t("incidenciasAlumno.tabla.alumno")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("incidenciasAlumno.tabla.incidencias")}</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => {
                  const det = detalles[r.alumnoId] ?? [];
                  return (
                    <React.Fragment key={r.alumnoId}>
                      {/* fila resumen */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleDetalle(r.alumnoId)}>
                            {openRow === r.alumnoId ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.alumno}</TableCell>
                        <TableCell align="right">{r.numInc}</TableCell>
                      </TableRow>

                      {/* filas detalle (reservas) */}
                      <TableRow>
                        {/* Aumenta colSpan a 4 porque la tabla detalle ahora tiene 4 columnas */}
                        <TableCell style={{ padding: 0 }} colSpan={3}>
                          <Collapse in={openRow === r.alumnoId} unmountOnExit>
                            <Paper variant="outlined" sx={{ m: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t("incidenciasAlumno.detalle.fecha")}</TableCell>
                                    <TableCell>{t("incidenciasAlumno.detalle.profesor")}</TableCell>
                                    <TableCell>
                                      {t<string>(
                                        "incidenciasAlumno.detalle.comentarios",
                                        "Comentarios internos"
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {t("incidenciasAlumno.detalle.ver")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {det.map((d) => {
                                    const comentario = extractComentario(d);
                                    return (
                                      <TableRow key={d.id}>
                                        <TableCell>{dayjs(d.fecha).format("YYYY-MM-DD")}</TableCell>
                                        <TableCell>{d.cliente /* profesor */}</TableCell>
                                        <TableCell sx={{ maxWidth: 520 }}>
                                          <MuiTooltip
                                            title={
                                              <span
                                                style={{
                                                  whiteSpace: "pre-line",
                                                }}
                                              >
                                                {comentario ||
                                                  t<string>(
                                                    "incidenciasAlumno.detalle.sinComentarios",
                                                    "—"
                                                  )}
                                              </span>
                                            }
                                            placement="top-start"
                                            arrow
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {comentario || "—"}
                                            </Typography>
                                          </MuiTooltip>
                                        </TableCell>
                                        <TableCell align="center">
                                          <IconButton
                                            size="small"
                                            onClick={() => abrirReserva(d.id)}
                                            aria-label={t<string>(
                                              "incidenciasAlumno.detalle.abrirReserva",
                                              "Abrir reserva"
                                            )}
                                          >
                                            <Visibility fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Paper>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* ─── diálogo de reserva ─── */}
      <ReservationFormDialog
        open={!!selId}
        mode="edit"
        readOnly
        reservaId={selId ?? undefined}
        initialValues={selData ?? undefined}
        onClose={() => setSelId(null)}
      >
        {loadingSel && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        )}
      </ReservationFormDialog>
    </Stack>
  );
}
